<template>
  <div class="relative size-full">
    <iframe
      ref="iframeEl"
      class="size-full border-0 bg-white"
      style="color-scheme: light"
    />

    <div
      v-if="source.error.value"
      class="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-red-100 px-4 py-2 text-center text-xs"
    >
      <div class="font-bold text-red-500">Error rendering preview</div>
      <div class="text-red-500">{{ errorMessage }}</div>
    </div>

    <div
      v-if="source.pending.value"
      class="absolute bottom-3 right-3 flex select-none items-center gap-1.5 rounded bg-muted px-2 py-1 text-xs"
    >
      <PendingIndicator pending /> Rendering...
    </div>
  </div>
</template>

<script setup lang="ts">
import nanomorph from "nanomorph";
import * as v from "valibot";
import {
  type EffectScope,
  computed,
  effectScope,
  onMounted,
  onScopeDispose,
  reactive,
  useTemplateRef,
  watch,
} from "vue";

import PendingIndicator from "@/components/utilities/PendingIndicator.vue";
import type { PreviewSource } from "@/lib/api/queries/preview/types";
import { SimpleEventEmitter } from "@/lib/events";

import { createSrcdoc } from "./frame/create-srcdoc";
import type { PreviewExtension, PreviewFrame } from "./frame/extension";
import { type ToMainPayload, toMainMessageSchema } from "./frame/protocol";

const props = defineProps<{
  source: PreviewSource;
  // Pass `true` for non-email contexts (web/archive/portal/subscribe) so
  // `/static` asset references resolve against buttondown.com instead of
  // the dev-server origin where they'd 404.
  rewriteStaticToProd?: boolean;
  // Plaintext-only newsletters: backend returns raw text (not HTML) for
  // the email medium. Wrap it in a `<pre>` so newlines + long lines
  // display readably instead of collapsing.
  wrapPlaintext?: boolean;
  extensions?: readonly PreviewExtension[];
}>();

const errorMessage = computed(() => {
  const err = props.source.error;
  return err instanceof Error ? err.message : String(err);
});

const iframeEl = useTemplateRef<HTMLIFrameElement>("iframeEl");

interface ActiveDocument {
  frameId: string;
  scriptCount: number;
  scope: EffectScope;
  dispatcher: SimpleEventEmitter<[ToMainPayload]>;
  readyNames: Set<string>;
  extensions: readonly PreviewExtension[];
  blobUrl: string;
}

let active: ActiveDocument | null = null;

const buildSrcdoc = (
  frameId: string,
  extensions: readonly PreviewExtension[]
) => {
  const html = props.source.html.value ?? "";

  const wrapped = props.wrapPlaintext
    ? `<pre style="text-wrap: wrap;">${html}</pre>`
    : html;

  return createSrcdoc({
    html: wrapped,
    frameId,
    extensions: extensions.map((ext) => ({
      name: ext.name,
      scriptUrl: ext.scriptUrl,
    })),
    rewriteStaticToProd: props.rewriteStaticToProd,
  });
};

const countScripts = (s: string) => s.match(/<script\b/gi)?.length ?? 0;

const sendIntoFrame = (payload: unknown) => {
  iframeEl.value?.contentWindow?.postMessage(payload, "*");
};

const teardown = (doc: ActiveDocument) => {
  doc.scope.stop();
  URL.revokeObjectURL(doc.blobUrl);
};

// The send queue holds messages emitted before the iframe-side script
// reports ready, so extensions can call frame.send() during setup().
const installExtensions = (doc: ActiveDocument) => {
  const sendQueue = new Map<string, unknown[]>();

  const flushQueue = (name: string) => {
    const queue = sendQueue.get(name);
    if (queue === undefined) {
      return;
    }
    sendQueue.delete(name);
    for (const payload of queue) {
      sendIntoFrame({ type: "extension:message", name, payload });
    }
  };

  doc.scope.run(() => {
    const unsubscribe = doc.dispatcher.subscribe((msg) => {
      if (msg.type === "extension:ready") {
        flushQueue(msg.name);
      }
    });
    onScopeDispose(unsubscribe);

    for (const ext of doc.extensions) {
      const extScope = effectScope();

      const frame: PreviewFrame = {
        id: doc.frameId,
        send(payload) {
          if (doc.readyNames.has(ext.name)) {
            sendIntoFrame({
              type: "extension:message",
              name: ext.name,
              payload,
            });
            return;
          }
          const queue = sendQueue.get(ext.name);
          if (queue === undefined) {
            sendQueue.set(ext.name, [payload]);
          } else {
            queue.push(payload);
          }
        },
      };

      const isReady = computed(() => doc.readyNames.has(ext.name));

      const onReady = (callback: () => void) => {
        extScope.run(() => {
          const unsubscribe = doc.dispatcher.subscribe((msg) => {
            if (msg.type === "extension:ready" && msg.name === ext.name) {
              callback();
            }
          });
          onScopeDispose(unsubscribe);
        });
      };

      const onMessage = (callback: (payload: unknown) => void) => {
        extScope.run(() => {
          const unsubscribe = doc.dispatcher.subscribe((msg) => {
            if (msg.type === "extension:message" && msg.name === ext.name) {
              callback(msg.payload);
            }
          });
          onScopeDispose(unsubscribe);
        });
      };

      try {
        extScope.run(() => {
          ext.setup({ frame, isReady, onReady, onMessage });
        });
      } catch (err) {
        extScope.stop();
        console.error(
          `[preview] extension "${ext.name}" failed to initialize`,
          err
        );
      }
    }
  });
};

// Consumers wanting a forced reload (mode/context switch, etc.) should
// `:key` the component itself rather than reaching for a prop here.
const rebuild = () => {
  if (import.meta.env.VITEST) {
    return;
  }

  const iframe = iframeEl.value;
  if (!iframe) {
    return;
  }

  const extensions = props.extensions ?? [];
  const frameId = crypto.randomUUID();
  const srcdoc = buildSrcdoc(frameId, extensions);
  const blob = new Blob([srcdoc], { type: "text/html" });
  const blobUrl = URL.createObjectURL(blob);

  const next: ActiveDocument = {
    frameId,
    scriptCount: countScripts(srcdoc),
    scope: effectScope(),
    dispatcher: new SimpleEventEmitter<[ToMainPayload]>(),
    readyNames: reactive(new Set<string>()),
    extensions,
    blobUrl,
  };

  if (active) {
    teardown(active);
  }
  active = next;

  installExtensions(next);
  iframe.contentWindow?.location.replace(blobUrl);
};

// nanomorph won't re-execute scripts, so any change in script count means
// the diff would silently miss new behavior — fall back to a full reload.
const tryMorph = () => {
  const doc = active;
  const iframe = iframeEl.value;
  if (!doc || !iframe) {
    rebuild();
    return;
  }

  const srcdoc = buildSrcdoc(doc.frameId, doc.extensions);

  if (countScripts(srcdoc) !== doc.scriptCount) {
    rebuild();
    return;
  }

  const liveRoot = iframe.contentDocument?.documentElement;
  if (!liveRoot) {
    rebuild();
    return;
  }

  let parsedRoot: HTMLElement | null;
  try {
    parsedRoot = new DOMParser().parseFromString(
      srcdoc,
      "text/html"
    ).documentElement;
  } catch (err) {
    console.warn("[preview] failed to parse srcdoc, falling back", err);
    rebuild();
    return;
  }

  if (!parsedRoot) {
    rebuild();
    return;
  }

  // Morph the <html> roots, not the Documents. Passing Documents to
  // nanomorph trips a HierarchyRequestError when it tries replaceChild
  // at document level (Documents accept only one element child).
  try {
    nanomorph(liveRoot, parsedRoot);
    sendIntoFrame({ type: "frame:morphed" });
  } catch (err) {
    console.warn("[preview] morph failed, falling back to reload", err);
    rebuild();
  }
};

const onWindowMessage = (ev: MessageEvent) => {
  if (ev.source !== iframeEl.value?.contentWindow) {
    return;
  }
  const result = v.safeParse(toMainMessageSchema, ev.data, {
    abortEarly: true,
  });
  if (!result.success) {
    return;
  }
  const { frameId, payload } = result.output;
  if (!active || active.frameId !== frameId) {
    return;
  }
  if (payload.type === "extension:ready") {
    active.readyNames.add(payload.name);
  }
  active.dispatcher.emit(payload);
};

window.addEventListener("message", onWindowMessage);

onScopeDispose(() => {
  window.removeEventListener("message", onWindowMessage);
  if (active) {
    teardown(active);
    active = null;
  }
});

onMounted(() => rebuild());

watch(
  () => props.extensions,
  () => rebuild()
);

watch(
  () => props.source.html.value,
  () => tryMorph()
);
</script>
